import { Box, Grid, Skeleton, Typography } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useTranslation } from "react-i18next";
import { SxProps } from "@mui/material";
import { OrganizationDetailDto } from "src/data/admin/types/organization";
import {
  colorSurface,
  colorBorder,
  colorTextPrimary,
  colorTextMuted,
  colorTextSecondary,
  shadowCard,
  radiusCard,
  iconBgInfo,
  iconBgSuccess,
  iconBgPurple,
  iconColorInfo,
  iconColorSuccess,
  iconColorPurple,
} from "src/design/tokens";

interface StatChipProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  iconBg: string;
  iconColor: string;
}

const StatChip = ({ icon, label, value, iconBg, iconColor }: StatChipProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.5,
      border: `1px solid ${colorBorder}`,
      borderRadius: 2,
      backgroundColor: colorSurface,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        backgroundColor: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: iconColor,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: colorTextPrimary, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: colorTextMuted }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

interface OrganizationSummaryProps {
  organization: OrganizationDetailDto | undefined;
  isLoading: boolean;
  sx?: SxProps;
}

export const OrganizationSummary = ({
  organization,
  isLoading,
  sx,
}: OrganizationSummaryProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: colorSurface,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        px: 3,
        py: 2.5,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: iconBgInfo,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: iconColorInfo,
            flexShrink: 0,
          }}
        >
          <BusinessIcon fontSize="small" />
        </Box>
        <Box>
          {isLoading ? (
            <Skeleton width={160} height={24} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, color: colorTextPrimary, lineHeight: 1.2 }}>
              {organization?.name ?? "—"}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: colorTextMuted }}>
            {t("myOrganization.summary.title")}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton variant="rounded" height={64} />
          ) : (
            <StatChip
              icon={<PeopleOutlineIcon fontSize="small" />}
              label={t("myOrganization.summary.staff")}
              value={organization?.staff.length ?? 0}
              iconBg={iconBgSuccess}
              iconColor={iconColorSuccess}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton variant="rounded" height={64} />
          ) : (
            <StatChip
              icon={<WorkOutlineIcon fontSize="small" />}
              label={t("myOrganization.summary.roles")}
              value={organization?.roles.length ?? 0}
              iconBg={iconBgPurple}
              iconColor={iconColorPurple}
            />
          )}
        </Grid>
      </Grid>

      {!isLoading && organization && (
        <Typography variant="caption" sx={{ color: colorTextSecondary, display: "block", mt: 1.5 }}>
          {t("myOrganization.summary.restaurants", {
            count: organization.restaurants.length,
          })}
        </Typography>
      )}
    </Box>
  );
};
